import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { sendApiRequest } from "../utils";
import {
  FiUser, FiPhone, FiMail, FiFileText, FiCheckCircle,
  FiXCircle, FiClock, FiUpload, FiEye, FiDownload,
  FiShield, FiAlertCircle, FiSave, FiArrowRight,
  FiUsers, FiCreditCard, FiBookOpen, FiCalendar
} from 'react-icons/fi';
import {
  FaIdCard, FaFileAlt, FaRegFilePdf, FaRegImage,
  FaCheck, FaTimes, FaSpinner, FaShieldAlt
} from 'react-icons/fa';

const UpdateKYC = () => {
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm();
  const [kycHistory, setKycHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({
    panScanCopy: null,
    aadhaarScanCopy: null,
    cancelChequeCopy: null
  });
  const userData = useSelector(state => state.user_info[0]?.user);

  // Fetch KYC history
  useEffect(() => {
    const fetchKYCHistory = async () => {
      try {
        const response = await sendApiRequest(`api/kyc/history/${userData?._id}`, {}, "GET");
        if (response.data.success) {
          setKycHistory(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching KYC history:', error);
      }
    };

    if (userData?._id) {
      fetchKYCHistory();
    }
  }, [userData?._id]);

  const handleFileSelect = (field, file) => {
    if (file && file[0]) {
      const selectedFile = file[0];
      if (selectedFile.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'File size should be less than 5MB',
          icon: 'warning',
          confirmButtonColor: '#6366f1'
        });
        return;
      }
      setSelectedFiles(prev => ({ ...prev, [field]: selectedFile }));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'prayagproperties');

    const cloudinaryResponse = await fetch(
      'https://api.cloudinary.com/v1_1/djmyigser/image/upload',
      {
        method: 'POST',
        body: formData
      }
    );

    const cloudinaryData = await cloudinaryResponse.json();
    if (!cloudinaryData.secure_url) {
      throw new Error('Failed to upload image to Cloudinary');
    }
    return cloudinaryData.secure_url;
  };

  const onSubmit = async (data) => {
    if (!selectedFiles.panScanCopy || !selectedFiles.aadhaarScanCopy || !selectedFiles.cancelChequeCopy) {
      Swal.fire({
        title: 'Missing Documents',
        text: 'Please upload all required documents',
        icon: 'warning',
        confirmButtonColor: '#6366f1'
      });
      return;
    }

    try {
      setLoading(true);
      setUploadProgress({ pan: 'uploading', aadhaar: 'uploading', cheque: 'uploading' });

      // Upload all images
      const [panUrl, aadhaarUrl, chequeUrl] = await Promise.all([
        uploadImage(selectedFiles.panScanCopy).then(url => {
          setUploadProgress(prev => ({ ...prev, pan: 'done' }));
          return url;
        }),
        uploadImage(selectedFiles.aadhaarScanCopy).then(url => {
          setUploadProgress(prev => ({ ...prev, aadhaar: 'done' }));
          return url;
        }),
        uploadImage(selectedFiles.cancelChequeCopy).then(url => {
          setUploadProgress(prev => ({ ...prev, cheque: 'done' }));
          return url;
        })
      ]);

      // Send KYC data to backend
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kyc/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          panNo: data.panNo,
          aadhaarNo: data.aadhaarNo,
          panScanCopy: panUrl,
          aadhaarScanCopy: aadhaarUrl,
          cancelChequeCopy: chequeUrl
        })
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'KYC Submitted Successfully!',
          text: 'Your KYC details have been submitted for verification.',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#6366f1',
          timer: 3000,
          timerProgressBar: true
        });
        reset();
        setSelectedFiles({
          panScanCopy: null,
          aadhaarScanCopy: null,
          cancelChequeCopy: null
        });
        
        // Refresh KYC history
        const historyResponse = await sendApiRequest(`api/kyc/history/${userData?._id}`, {}, "GET");
        if (historyResponse.data.success) {
          setKycHistory(historyResponse.data.data);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: result.message || 'Failed to update KYC details',
          confirmButtonColor: '#ef4444'
        });
      }
    } catch (error) {
      console.error('Error updating KYC:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while updating KYC details',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'Pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved': return <FiCheckCircle className="w-4 h-4" />;
      case 'Rejected': return <FiXCircle className="w-4 h-4" />;
      case 'Pending': return <FiClock className="w-4 h-4" />;
      default: return <FiAlertCircle className="w-4 h-4" />;
    }
  };

  const inputClasses = "w-full px-4 py-3 text-base rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                KYC Verification
              </h1>
              <p className="text-gray-500 mt-1 text-sm">Submit your documents for verification</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Secure Submission</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* KYC Form - Main Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
            >
              {/* Form Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <FaShieldAlt className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-semibold text-lg">KYC Documentation</h2>
                    <p className="text-indigo-100 text-sm">Please provide accurate information</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                {/* Personal Information - Read Only */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiUser className="w-4 h-4 text-indigo-500" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Full Name</label>
                      <p className="text-gray-800 font-medium">{userData?.fullName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Mobile Number</label>
                      <p className="text-gray-800 font-medium">{userData?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">User ID</label>
                      <p className="text-gray-800 font-medium">{userData?.sponsorCode || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* KYC Documents */}
                <div className="space-y-5">
                  <h3 className="text-md font-semibold text-gray-800 flex items-center gap-2">
                    <FiFileText className="w-4 h-4 text-indigo-500" />
                    Document Details
                  </h3>

                  {/* PAN Card */}
                  <div className="border border-gray-200 rounded-xl p-5 hover:border-indigo-200 transition">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                        <FaIdCard className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">PAN Card</h4>
                        <p className="text-xs text-gray-500">Permanent Account Number</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>PAN Number <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          {...register("panNo", { 
                            required: "PAN number is required",
                            pattern: {
                              value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                              message: "Invalid PAN format (e.g., ABCDE1234F)"
                            }
                          })}
                          className={`${inputClasses} ${errors.panNo ? 'border-red-500' : ''}`}
                          placeholder="Enter PAN number"
                        />
                        {errors.panNo && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <FiAlertCircle className="w-3 h-3" />
                            {errors.panNo.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelClasses}>PAN Scan Copy <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect('panScanCopy', e.target.files)}
                            className="w-full px-4 py-3 text-base rounded-xl border border-gray-200 transition-all outline-none bg-gray-50/50 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
                          />
                          {uploadProgress.pan === 'uploading' && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <FaSpinner className="w-5 h-5 text-indigo-500 animate-spin" />
                            </div>
                          )}
                          {uploadProgress.pan === 'done' && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <FaCheck className="w-5 h-5 text-emerald-500" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Supported formats: JPG, PNG, PDF (Max 5MB)</p>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar Card */}
                  <div className="border border-gray-200 rounded-xl p-5 hover:border-indigo-200 transition">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <FaIdCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Aadhaar Card</h4>
                        <p className="text-xs text-gray-500">Unique Identification Number</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>Aadhaar Number <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          {...register("aadhaarNo", { 
                            required: "Aadhaar number is required",
                            pattern: {
                              value: /^\d{12}$/,
                              message: "Aadhaar number must be 12 digits"
                            }
                          })}
                          className={`${inputClasses} ${errors.aadhaarNo ? 'border-red-500' : ''}`}
                          placeholder="Enter Aadhaar number"
                        />
                        {errors.aadhaarNo && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <FiAlertCircle className="w-3 h-3" />
                            {errors.aadhaarNo.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelClasses}>Aadhaar Scan Copy <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect('aadhaarScanCopy', e.target.files)}
                            className="w-full px-4 py-3 text-base rounded-xl border border-gray-200 transition-all outline-none bg-gray-50/50 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
                          />
                          {uploadProgress.aadhaar === 'uploading' && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <FaSpinner className="w-5 h-5 text-indigo-500 animate-spin" />
                            </div>
                          )}
                          {uploadProgress.aadhaar === 'done' && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <FaCheck className="w-5 h-5 text-emerald-500" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Supported formats: JPG, PNG, PDF (Max 5MB)</p>
                      </div>
                    </div>
                  </div>

                  {/* Cancel Cheque */}
                  <div className="border border-gray-200 rounded-xl p-5 hover:border-indigo-200 transition">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <FiCreditCard className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Cancel Cheque</h4>
                        <p className="text-xs text-gray-500">Bank account verification</p>
                      </div>
                    </div>
                    <div>
                      <label className={labelClasses}>Cancel Cheque Copy <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileSelect('cancelChequeCopy', e.target.files)}
                          className="w-full px-4 py-3 text-base rounded-xl border border-gray-200 transition-all outline-none bg-gray-50/50 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
                        />
                        {uploadProgress.cheque === 'uploading' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <FaSpinner className="w-5 h-5 text-indigo-500 animate-spin" />
                          </div>
                        )}
                        {uploadProgress.cheque === 'done' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <FaCheck className="w-5 h-5 text-emerald-500" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Upload a clear image of your cancelled cheque</p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold 
                    transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl
                    disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="w-5 h-5 animate-spin" />
                        Submitting KYC...
                      </>
                    ) : (
                      <>
                        <FiSave className="w-5 h-5" />
                        Submit KYC for Verification
                        <FiArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>

          {/* KYC History Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 sticky top-6"
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
                <div className="flex items-center gap-2">
                  <FiClock className="w-5 h-5 text-white" />
                  <h3 className="text-white font-semibold">KYC History</h3>
                </div>
                <p className="text-indigo-100 text-xs mt-1">Previous submissions</p>
              </div>

              <div className="max-h-[500px] overflow-y-auto">
                {kycHistory.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {kycHistory.map((kyc, index) => (
                      <motion.div
                        key={kyc._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <FiCalendar className="w-3 h-3" />
                            {new Date(kyc.createdAt).toLocaleDateString('en-IN')}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(kyc.status)}`}>
                            {getStatusIcon(kyc.status)}
                            {kyc.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600"><span className="font-medium">PAN:</span> {kyc.panNo}</p>
                          <p className="text-gray-600"><span className="font-medium">Aadhaar:</span> {kyc.aadhaarNo}</p>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <a
                            href={kyc.panScanCopy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            <FiEye className="w-3 h-3" /> PAN
                          </a>
                          <a
                            href={kyc.aadhaarScanCopy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            <FiEye className="w-3 h-3" /> Aadhaar
                          </a>
                          <a
                            href={kyc.cancelChequeCopy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            <FiEye className="w-3 h-3" /> Cheque
                          </a>
                        </div>
                        {kyc.remarks && (
                          <p className="text-xs text-gray-400 mt-2 border-t border-gray-100 pt-2">
                            Remarks: {kyc.remarks}
                          </p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                      <FiFileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-sm">No KYC history available</p>
                    <p className="text-xs text-gray-400 mt-1">Submit your first KYC</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Security Notice */}
            <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <div className="flex items-start gap-2">
                <FiShield className="w-4 h-4 text-indigo-600 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-indigo-800">Secure & Encrypted</p>
                  <p className="text-xs text-indigo-600 mt-1">Your documents are securely stored and encrypted. Only authorized personnel can access them.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateKYC;